import Form from "@/components/Form";
import debounce from "@/utils/debounce";

import "./style.css";

const UK_POSTCODE_REG_EX =
  /([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/;

export default class BookValuationForm {
  static selector = ".bv-form";

  constructor(block) {
    this.block = block;

    this.formElement = this.block.querySelector(".form");

    this.form = new Form(this.formElement, {
      onSubmitSucceeded: this.handleSubmitSucceeded,
      submitLoadingLabel: "Booking...",
      submitSuccessLabel: "Booked",
      resetAfterSubmit: false,
      gainControlOnSubmitSucceeded: false,
    });

    // get address fields
    this.postcodeField = this.formElement.querySelector("[name=postcode]");
    this.manualAddressCheckbox = this.formElement.querySelector(
      "[name=checkbox-manual-address]"
    );
    this.addressField = this.formElement.querySelector("[name=address]");
    this.address2Field = this.formElement.querySelector("[name=address2]");
    this.townField = this.formElement.querySelector("[name=town]");
    this.countyField = this.formElement.querySelector("[name=county]");

    // get query results list
    this.addressPartsWrappers = this.formElement.querySelectorAll(
      ".bv-form__address-parts"
    );
    this.addressesList = this.formElement.querySelector(".bv-form__addresses");
    this.isAddressIoEnabled = true;
    this.previousValue = "";
    this.addressesLookupCache = {};
  }

  getAddressesByPostcode = async (postcode) => {
    const apiKey = window.wpData.addressIOApiKey;
    const method = "get";
    const url =
      "https://api.getAddress.io/find/" + postcode + "?api-key=" + apiKey;

    if (this.addressesLookupCache[postcode]) {
      return this.addressesLookupCache[postcode];
    }

    const response = await fetch(url, { method });

    if (response.status !== 200) {
      console.log("Error " + response.status + ": " + response.statusText);
      throw new Error("Failed to get addresses");
    }

    const json = await response.json();

    const addresses = json.addresses.map((a) => {
      const readableAddress = a.replace(/(?:, )+/g, ", ");
      const parts = a.split(/\s*,\s*/).map((p) => p.trim());
      let street = readableAddress;
      let address2 = "";
      let town = "";
      let county = "";

      if (parts.length === 7 && !!parts[0]) {
        street = parts[0];
        if (!!parts[1]) {
          street += ", " + parts[1];
        }
        if (!!parts[2]) {
          street += ", " + parts[2];
        }
        if (!!parts[3]) {
          address2 = parts[3];
        }
        town = parts[5];
        if (!!parts[4]) {
          town = parts[4] + ", " + town;
        }
        county = parts[6] || parts[5];
      }

      return {
        readableAddress: readableAddress,
        street: street,
        address2: address2,
        town: town,
        county: county,
      };
    });

    this.addressesLookupCache[postcode] = addresses;
    return addresses;
  };

  selectPreviousSuggestion() {
    const activeSuggestion = this.addressesList.querySelector(
      ".bv-form__address-result--active"
    );

    let newActiveSuggestion = null;
    if (activeSuggestion) {
      if (activeSuggestion.previousElementSibling) {
        activeSuggestion.classList.remove("bv-form__address-result--active");
        newActiveSuggestion = activeSuggestion.previousElementSibling;
      }
    } else {
      newActiveSuggestion = this.addressesList.firstElementChild;
    }

    if (newActiveSuggestion) {
      newActiveSuggestion.classList.add("bv-form__address-result--active");
      newActiveSuggestion.scrollIntoView({
        block: "center",
      });
    }
  }
  selectNextSuggestion() {
    const activeSuggestion = this.addressesList.querySelector(
      ".bv-form__address-result--active"
    );

    let newActiveSuggestion = null;
    if (activeSuggestion) {
      if (activeSuggestion.nextElementSibling) {
        activeSuggestion.classList.remove("bv-form__address-result--active");
        newActiveSuggestion = activeSuggestion.nextElementSibling;
      }
    } else {
      newActiveSuggestion = this.addressesList.firstElementChild;
    }

    if (newActiveSuggestion) {
      newActiveSuggestion.classList.add("bv-form__address-result--active");
      newActiveSuggestion.scrollIntoView({
        block: "center",
      });
    }
  }

  handleSubmitSucceeded = (response) => {
    console.log("Success!", response);

    window.App.modals.valuationSuccessModal.open();
  };

  handleManualAddressCheckboxChange = () => {
    if (this.manualAddressCheckbox.checked) {
      this.addressPartsWrappers.forEach((wrapper) => {
        wrapper.classList.add("bv-form__address-parts--visible");
      });
    } else {
      this.addressPartsWrappers.forEach((wrapper) => {
        wrapper.classList.remove("bv-form__address-parts--visible");
      });
    }
  };

  handlePostCodeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const activeAddressResult = this.addressesList.querySelector(
        ".bv-form__address-result--active"
      );
      if (!activeAddressResult) {
        return;
      }

      activeAddressResult.click();
      this.postcodeField.blur();
    }
  };

  handleSuggestionsNavigation = (e) => {
    if (!this.isAddressIoEnabled) {
      return;
    }

    const key = e.key;
    if (key === "ArrowUp") {
      this.selectPreviousSuggestion();
    } else if (key === "ArrowDown") {
      this.selectNextSuggestion();
    } else if (key === "Escape") {
      this.postcodeField.blur();
    }
  };

  handlePostCodeChange = debounce(() => {
    if (!this.isAddressIoEnabled) {
      return;
    }

    const value = this.postcodeField.value;

    if (value === this.previousValue) {
      return;
    } else {
      this.previousValue = value;
    }

    if (value && UK_POSTCODE_REG_EX.test(value)) {
      // it's a uk postcode
      this.getAddressesByPostcode(value)
        .then((addresses) => {
          this.addressesList.innerHTML = "";

          for (let i = 0; i < addresses.length; i++) {
            const address = addresses[i];

            const li = document.createElement("li");
            li.classList.add("bv-form__address-result");
            li.innerHTML = address.readableAddress;
            li.addEventListener("click", () => {
              this.addressField.value = address.street;
              this.address2Field.value = address.address2;
              this.townField.value = address.town;
              this.countyField.value = address.county;
              this.postcodeField.blur();
              this.manualAddressCheckbox.checked = true;

              this.addressPartsWrappers.forEach((wrapper) => {
                wrapper.classList.add("bv-form__address-parts--visible");
              });
            });

            this.addressesList.appendChild(li);
          }

          if (this.addressesList.childElementCount) {
            this.addressesList.classList.add("bv-form__addresses--visible");
          }
        })
        .catch((e) => {
          console.log("Error fetching addresses", e);
        });
    }
  }, 1000);

  handlePostCodeBlur = () => {
    if (!this.isAddressIoEnabled) {
      return;
    }
    setTimeout(() => {
      this.addressesList.classList.remove("bv-form__addresses--visible");
    }, 200);
  };

  handlePostCodeFocus = () => {
    if (!this.isAddressIoEnabled) {
      return;
    }

    const value = this.postcodeField.value;

    if (
      value &&
      UK_POSTCODE_REG_EX.test(value) &&
      this.addressesList.childElementCount
    ) {
      this.addressesList.classList.add("bv-form__addresses--visible");
    }
  };

  onReady = () => {
    this.postcodeField.addEventListener("keydown", this.handlePostCodeKeyDown);
    this.postcodeField.addEventListener(
      "keyup",
      this.handleSuggestionsNavigation
    );
    this.postcodeField.addEventListener("keyup", this.handlePostCodeChange);
    this.postcodeField.addEventListener("blur", this.handlePostCodeBlur);
    this.postcodeField.addEventListener("focus", this.handlePostCodeFocus);
    this.manualAddressCheckbox.addEventListener(
      "change",
      this.handleManualAddressCheckboxChange
    );
  };

  onDestroy = () => {
    this.postcodeField.removeEventListener(
      "keydown",
      this.handlePostCodeKeyDown
    );
    this.postcodeField.removeEventListener(
      "keyup",
      this.handleSuggestionsNavigation
    );
    this.postcodeField.removeEventListener("keyup", this.handlePostCodeChange);
    this.postcodeField.removeEventListener("blur", this.handlePostCodeBlur);
    this.postcodeField.removeEventListener("focus", this.handlePostCodeFocus);
    this.manualAddressCheckbox.removeEventListener(
      "change",
      this.handleManualAddressCheckboxChange
    );
  };
}
